import { Controller, type Path, type UseFieldArrayReturn } from 'react-hook-form'
import { Trash2 } from '@/components/icons'
import { defectActionLabels, defectActionOptions } from '@@api/flightLog.shared'
import type { FlightLogFormData } from '../form'
import type { FlightLogSectionContext } from '../useFlightLogForm'
import { TextRow, TextareaField } from '../../ui/prntr-fields'
import { AddPillButton } from '../rows'
import { RichToggleRow, SegmentedSwitch } from '../SegmentedSwitch'

const emptyDefect = () => ({ description: '', action: 'A' as const, melRef: null, actionNotes: null })

export function DefectsSection({
  context,
  defectArray,
}: {
  context: FlightLogSectionContext
  defectArray: UseFieldArrayReturn<FlightLogFormData, 'defects.entries'>
}) {
  const { control, values, readOnly, fieldError } = context
  return (
    <>
      <Controller
        control={control}
        name="defects.hasDefects"
        render={({ field }) => (
          <RichToggleRow
            title="Any defects to report?"
            checked={field.value}
            onChange={(checked) => {
              field.onChange(checked)
              if (checked && defectArray.fields.length === 0) {
                defectArray.append(emptyDefect())
              }
            }}
            readOnly={readOnly}
          />
        )}
      />
      {fieldError('defects') && (
        <p className="pt-2 text-xs text-content-negative-bold">{fieldError('defects')}</p>
      )}
      {values.defects.hasDefects && (
        <>
          {defectArray.fields.map((row, index) => (
            <div key={row.id} className="flex flex-col gap-2 pt-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-content-negative-bold">
                  Defect {index + 1}
                </p>
                {!readOnly && (
                  <button
                    type="button"
                    className="flex size-6 items-center justify-center"
                    onClick={() => defectArray.remove(index)}
                  >
                    <Trash2 className="size-4 text-content-primary-subtle" />
                  </button>
                )}
              </div>
              <TextareaField
                control={control}
                name={`defects.entries.${index}.description` as Path<FlightLogFormData>}
                label="Description"
                placeholder="Describe the defect"
                readOnly={readOnly}
                error={fieldError(`defects.entries.${index}.description`)}
              />
              <Controller
                control={control}
                name={`defects.entries.${index}.action` as Path<FlightLogFormData>}
                render={({ field }) => (
                  <SegmentedSwitch
                    options={defectActionOptions}
                    value={(field.value as 'A' | 'D') ?? null}
                    onChange={field.onChange}
                    labels={defectActionLabels}
                    readOnly={readOnly}
                  />
                )}
              />
              <TextRow
                control={control}
                name={`defects.entries.${index}.melRef` as Path<FlightLogFormData>}
                label="MEL Ref"
                placeholder="Optional"
                readOnly={readOnly}
              />
              <TextareaField
                control={control}
                name={`defects.entries.${index}.actionNotes` as Path<FlightLogFormData>}
                label="Action taken by engineer/pilot"
                placeholder="Describe the action taken"
                readOnly={readOnly}
              />
            </div>
          ))}
          {!readOnly && (
            <AddPillButton label="Add Defect" onClick={() => defectArray.append(emptyDefect())} />
          )}
        </>
      )}
    </>
  )
}
